import type {
  LensEvent,
  RenderEvent,
  ComponentId,
  ComponentInstance,
  EventId,
  InteractionId,
  RenderId,
  CommitId,
  CommitSnapshot,
  RenderSnapshot,
  EventsBatchMessage,
} from "@reactlens/protocol";
import { RingBuffer } from "./ring-buffer.js";
import { buildInteractions, type Interaction } from "./interactions.js";
import { TimelineIndex, causeFromReasons, type RenderFlag } from "./columnar.js";
import { FlatTreeIndex } from "./flat-tree.js";
import {
  activityIntervals,
  hitTest,
  queryTimeline,
  statsPairInRange,
  statsInRange,
  type HitTestResult,
  type HitTestOptions,
  type RegionStatsPair,
  type TimelineQuery,
  type TimelineQueryResult,
} from "./aggregates.js";
import {
  RetentionManager,
  createMemoryColdStore,
  sliceToChunk,
  type ColdStore,
  type RetentionConfig,
} from "./retention.js";

/** A single commit pass: which components rendered, when. */
export interface CommitSummary {
  commitId: CommitId;
  timestamp: number;
  /** Latest render timestamp in the commit (≥ timestamp). */
  endTimestamp: number;
  componentIds: ComponentId[];
  interactionId?: InteractionId;
  totalSelfTime: number;
}

export interface TraceStoreConfig {
  maxEvents: number;
  maxRendersPerComponent: number;
  maxSnapshots: number;
  maxCommits: number;
  /** Whole-page DOM snapshots retained for offline replay. */
  maxCommitSnapshots: number;
  /**
   * Sliding time window: drop events older than `newest - maxAgeMs`. Null keeps
   * everything the count caps allow. A count cap alone is a poor fit for an app
   * that churns in the background — it silently trades the interesting minute
   * for a thousand idle commits.
   */
  maxAgeMs: number | null;
}

const DEFAULTS: TraceStoreConfig = {
  // Event ring is for export / interaction rebuild — columnar index is the
  // timeline source of truth and is not capped by this (see retention tiers).
  maxEvents: 100_000,
  maxRendersPerComponent: 100,
  maxSnapshots: 5_000,
  maxCommits: 1_000,
  maxCommitSnapshots: 300,
  maxAgeMs: null,
};

/** Minimum clip duration (ms) — matches timeline MIN_CLIP_MS. */
const MIN_CLIP_MS = 0.05;

/**
 * What the retention caps took. Every number here is loss the user cannot
 * otherwise see: a timeline that begins mid-session looks exactly like an app
 * that was idle, and a component whose early renders were evicted looks like
 * one that never rendered.
 */
export interface RetentionAccount {
  droppedEvents: number;
  droppedRenders: number;
  droppedSnapshots: number;
  /** Timestamp of the oldest event still held, or null when empty. */
  oldestRetainedAt: number | null;
}

interface MutableCommit {
  commitId: CommitId;
  timestamp: number;
  endTimestamp: number;
  components: Set<ComponentId>;
  interactionId?: InteractionId;
  totalSelfTime: number;
}

export type TraceSelector =
  | { kind: "component"; id: ComponentId }
  | { kind: "interaction"; id: InteractionId }
  | { kind: "global" };

export type Dispose = () => void;

interface Subscription {
  selector: TraceSelector;
  callback: () => void;
}

/**
 * The single source of truth on the panel side: a normalized, capped event log
 * plus per-component render history and snapshots. Framework-free and
 * unit-testable. The panel subscribes to narrow slices; high-frequency
 * ingestion never touches React state.
 */
export class TraceStore {
  private config: TraceStoreConfig;
  private events: RingBuffer<LensEvent>;
  private readonly rendersByComponent = new Map<ComponentId, RingBuffer<RenderEvent>>();
  private readonly rendersById = new Map<RenderId, RenderEvent>();
  private readonly snapshots = new Map<RenderId, RenderSnapshot>();
  private snapshotOrder: RingBuffer<RenderId>;
  private readonly instances = new Map<ComponentId, ComponentInstance>();
  /** Uncapped lifetime render count per component (rendersOf is capped). */
  private readonly renderTotals = new Map<ComponentId, number>();
  private readonly selfTimeTotals = new Map<ComponentId, number>();
  private readonly eventsByInteractionId = new Map<InteractionId, LensEvent[]>();
  /** Ids currently in the event ring — replays must not double-count. */
  private readonly seenEventIds = new Set<EventId>();
  private readonly commitsById = new Map<CommitId, MutableCommit>();
  private commitOrder: RingBuffer<CommitId>;
  /** Throttled whole-page DOM per commit (offline replay), oldest→newest. */
  private commitSnapshots: RingBuffer<CommitSnapshot>;
  /** Set when the event ring overwrites a render — commits rebuild on next read/ingest end. */
  private commitsDirty = false;
  /** Materialized commits(), invalidated whenever any commit mutates. */
  private commitsCache: CommitSummary[] | null = null;
  /** Running account of what the caps evicted — see `retention()`. */
  private dropped = { events: 0, renders: 0, snapshots: 0 };
  private readonly subscriptions = new Set<Subscription>();
  private readonly ingestObservers = new Set<(batch: EventsBatchMessage["payload"]) => void>();

  /**
   * Columnar timeline index — authoritative for viewport queries. Survives
   * beyond the event ring via HOT/WARM retention; not rebuilt on each query.
   */
  readonly timelineIndex = new TimelineIndex();
  /** Flattened ownership tree for virtualized tree queries. */
  readonly flatTree = new FlatTreeIndex();
  /** Tiered spill manager (WARM→COLD). */
  readonly retentionManager: RetentionManager;
  private treeDirty = false;

  constructor(config?: Partial<TraceStoreConfig>, coldStore?: ColdStore) {
    this.config = { ...DEFAULTS, ...config };
    this.events = new RingBuffer<LensEvent>(this.config.maxEvents);
    this.snapshotOrder = new RingBuffer<RenderId>(this.config.maxSnapshots);
    this.commitOrder = new RingBuffer<CommitId>(this.config.maxCommits);
    this.commitSnapshots = new RingBuffer<CommitSnapshot>(this.config.maxCommitSnapshots);
    this.retentionManager = new RetentionManager(coldStore ?? createMemoryColdStore());
  }

  ingest(batch: EventsBatchMessage["payload"]): void {
    for (const instance of batch.instances) {
      this.instances.set(instance.id, instance);
      this.flatTree.upsert({
        id: instance.id as number,
        ...(instance.parentId !== undefined ? { parentId: instance.parentId as number } : {}),
        name: instance.name,
        renders: this.renderTotals.get(instance.id) ?? 0,
        selfTime: this.selfTimeTotals.get(instance.id) ?? 0,
        compiled: instance.compiler.compiled,
      });
      this.treeDirty = true;
    }
    const touched = new Set<ComponentId>();
    const touchedInteractions = new Set<InteractionId>();
    for (const snapshot of batch.snapshots) {
      this.addSnapshot(snapshot);
      // On-demand snapshots arrive in their own batch with no events; mark the
      // component touched so subscribers (the Inspector) re-render and read it.
      touched.add(snapshot.componentId);
    }
    for (const cs of batch.commitSnapshots ?? []) {
      this.commitSnapshots.push(cs);
    }
    for (const event of batch.events) {
      this.addEvent(event);
      if (event.componentId !== undefined) touched.add(event.componentId);
      if (event.interactionId !== undefined) touchedInteractions.add(event.interactionId);
    }
    if (this.treeDirty) {
      // Refresh counters on touched instances then rebuild adjacency.
      for (const id of touched) {
        const inst = this.instances.get(id);
        if (!inst) continue;
        this.flatTree.upsert({
          id: inst.id as number,
          ...(inst.parentId !== undefined ? { parentId: inst.parentId as number } : {}),
          name: inst.name,
          renders: this.renderTotals.get(id) ?? 0,
          selfTime: this.selfTimeTotals.get(id) ?? 0,
          compiled: inst.compiler.compiled,
        });
      }
      this.flatTree.rebuildOrder();
      this.treeDirty = false;
    }
    this.trimByAge();
    this.maybeSpillWarm();
    if (this.commitsDirty) this.rebuildCommitsFromEvents();
    this.notify(touched, touchedInteractions);
    for (const observer of this.ingestObservers) observer(batch);
  }

  /**
   * Observe every ingested batch — used to tee frames into the Doctor worker's
   * mirror store. Distinct from `subscribe`, which only signals that a slice
   * changed; this carries the raw batch.
   */
  onIngest(cb: (batch: EventsBatchMessage["payload"]) => void): Dispose {
    this.ingestObservers.add(cb);
    return () => {
      this.ingestObservers.delete(cb);
    };
  }

  /**
   * One-shot snapshot of everything captured so far, shaped as an ingestable
   * batch. Lets a late-attaching consumer (the Doctor worker) backfill history
   * that was ingested before it subscribed.
   */
  export(): EventsBatchMessage["payload"] {
    return {
      events: this.events.toArray(),
      snapshots: [...this.snapshots.values()],
      instances: [...this.instances.values()],
      commitSnapshots: this.commitSnapshots.toArray(),
    };
  }

  private addEvent(event: LensEvent): void {
    // Idempotent on event id: the content-script buffer can replay (e.g. after
    // a panel reconnect), and re-ingesting must not double-count. Renders are
    // also keyed by renderId, which snapshots and time travel resolve against.
    if (this.seenEventIds.has(event.id)) return;
    if (event.type === "render" && this.rendersById.has(event.renderId)) return;
    this.seenEventIds.add(event.id);
    const evicted = this.events.push(event);
    if (evicted) {
      this.dropped.events++;
      if (evicted.type === "render") this.dropped.renders++;
      this.forgetEvent(evicted);
    }
    if (event.type === "render") {
      const buf =
        this.rendersByComponent.get(event.componentId) ??
        this.createRenderBuffer(event.componentId);
      const displaced = buf.push(event);
      if (displaced) {
        // The event ring may still hold it; this cap is the per-component
        // history the inspector reads, and it fills long before the ring does.
        this.dropped.renders++;
        this.rendersById.delete(displaced.renderId);
      }
      this.rendersById.set(event.renderId, event);
      this.renderTotals.set(event.componentId, (this.renderTotals.get(event.componentId) ?? 0) + 1);
      this.selfTimeTotals.set(
        event.componentId,
        (this.selfTimeTotals.get(event.componentId) ?? 0) + event.selfDuration,
      );
      this.recordCommit(event);
      this.appendTimeline(event);
      this.treeDirty = true;
    }
    if (event.interactionId !== undefined) {
      const list = this.eventsByInteractionId.get(event.interactionId) ?? [];
      list.push(event);
      this.eventsByInteractionId.set(event.interactionId, list);
    }
  }

  /**
   * Drop indexes that pointed at an event the ring just overwrote. Commits and
   * interactions both derive from the live event log — without this, commit
   * summaries outlive their renders and show up in the timeline idle gutter.
   */
  private forgetEvent(event: LensEvent): void {
    this.seenEventIds.delete(event.id);
    if (event.interactionId !== undefined) {
      const list = this.eventsByInteractionId.get(event.interactionId);
      if (list) {
        const next = list.filter((e) => e.id !== event.id);
        if (next.length === 0) this.eventsByInteractionId.delete(event.interactionId);
        else this.eventsByInteractionId.set(event.interactionId, next);
      }
    }
    if (event.type !== "render") return;
    // Only clear if this renderId still maps to the evicted event (not a newer ingest).
    if (this.rendersById.get(event.renderId) === event) {
      this.rendersById.delete(event.renderId);
    }
    this.commitsDirty = true;
  }

  /**
   * Change retention at runtime (the panel's History settings). Shrinking a cap
   * keeps the newest entries; widening one just makes room.
   */
  configure(next: Partial<TraceStoreConfig>): void {
    const before = this.config;
    this.config = { ...before, ...next };

    if (this.config.maxSnapshots !== before.maxSnapshots) {
      this.snapshotOrder = resized(this.snapshotOrder, this.config.maxSnapshots);
      const kept = new Set(this.snapshotOrder.toArray());
      for (const id of [...this.snapshots.keys()]) {
        if (!kept.has(id)) this.snapshots.delete(id);
      }
    }
    if (this.config.maxCommitSnapshots !== before.maxCommitSnapshots) {
      this.commitSnapshots = resized(this.commitSnapshots, this.config.maxCommitSnapshots);
    }
    if (this.config.maxCommits !== before.maxCommits) {
      this.commitOrder = resized(this.commitOrder, this.config.maxCommits);
      this.commitsDirty = true;
    }
    if (
      this.config.maxEvents !== before.maxEvents ||
      this.config.maxRendersPerComponent !== before.maxRendersPerComponent
    ) {
      const kept = this.events.toArray().slice(-this.config.maxEvents);
      this.events = new RingBuffer<LensEvent>(this.config.maxEvents);
      this.reindexEvents(kept);
    }
    this.trimByAge();
    if (this.commitsDirty) this.rebuildCommitsFromEvents();
    this.commitsCache = null;
    this.notify(new Set(), new Set());
  }

  /** Drop events that fell out of the `maxAgeMs` window behind the newest one. */
  private trimByAge(): void {
    const maxAge = this.config.maxAgeMs;
    if (maxAge === null || this.events.size === 0) return;
    const newest = this.events.at(this.events.size - 1)!.timestamp;
    const cutoff = newest - maxAge;
    if (this.events.at(0)!.timestamp >= cutoff) return;
    this.reindexEvents(this.events.toArray().filter((e) => e.timestamp >= cutoff));
  }

  /**
   * Rebuild every event-derived index from `events`. Lifetime totals
   * (`renderTotals` / `selfTimeTotals`) are deliberately untouched — they count
   * what the app did, not what we still retain.
   */
  private reindexEvents(events: readonly LensEvent[]): void {
    const lost = this.events.size - events.length;
    if (lost > 0) {
      this.dropped.events += lost;
      const kept = new Set(events.map((e) => e.id));
      for (const event of this.events.toArray()) {
        if (event.type === "render" && !kept.has(event.id)) this.dropped.renders++;
      }
    }
    this.events.clear();
    this.rendersByComponent.clear();
    this.rendersById.clear();
    this.eventsByInteractionId.clear();
    this.seenEventIds.clear();
    for (const event of events) {
      this.events.push(event);
      this.seenEventIds.add(event.id);
      if (event.type === "render") {
        const buf =
          this.rendersByComponent.get(event.componentId) ??
          this.createRenderBuffer(event.componentId);
        buf.push(event);
        this.rendersById.set(event.renderId, event);
      }
      if (event.interactionId !== undefined) {
        const list = this.eventsByInteractionId.get(event.interactionId) ?? [];
        list.push(event);
        this.eventsByInteractionId.set(event.interactionId, list);
      }
    }
    this.commitsDirty = true;
  }

  /** Re-derive commit summaries from whatever renders remain in the event ring. */
  private rebuildCommitsFromEvents(): void {
    this.commitsCache = null;
    this.commitsById.clear();
    this.commitOrder.clear();
    for (const e of this.events.toArray()) {
      if (e.type === "render") this.recordCommit(e);
    }
    this.commitsDirty = false;
  }

  private recordCommit(event: RenderEvent): void {
    this.commitsCache = null;
    let commit = this.commitsById.get(event.commitId);
    if (!commit) {
      // Evict the oldest commit when the ring wraps.
      if (this.commitOrder.size >= this.config.maxCommits) {
        const evicted = this.commitOrder.toArray()[0];
        if (evicted !== undefined) this.commitsById.delete(evicted);
      }
      commit = {
        commitId: event.commitId,
        timestamp: event.timestamp,
        endTimestamp: event.timestamp,
        components: new Set(),
        totalSelfTime: 0,
        ...(event.interactionId !== undefined ? { interactionId: event.interactionId } : {}),
      };
      this.commitsById.set(event.commitId, commit);
      this.commitOrder.push(event.commitId);
    }
    commit.components.add(event.componentId);
    commit.totalSelfTime += event.selfDuration;
    // Renders in one commit share `timestamp`; fold durations so the commit
    // span (and session length) reflect real work, not a zero-width instant.
    const end = event.timestamp + Math.max(event.selfDuration, event.totalDuration, 0);
    commit.endTimestamp = Math.max(commit.endTimestamp, end);
  }

  private createRenderBuffer(id: ComponentId): RingBuffer<RenderEvent> {
    const buf = new RingBuffer<RenderEvent>(this.config.maxRendersPerComponent);
    this.rendersByComponent.set(id, buf);
    return buf;
  }

  private addSnapshot(snapshot: RenderSnapshot): void {
    // Evict the oldest snapshot when the ring wraps, so the Map stays bounded.
    if (this.snapshotOrder.size >= this.config.maxSnapshots) {
      const evicted = this.snapshotOrder.toArray()[0];
      if (evicted !== undefined && this.snapshots.delete(evicted)) this.dropped.snapshots++;
    }
    this.snapshots.set(snapshot.renderId, snapshot);
    this.snapshotOrder.push(snapshot.renderId);
  }

  // ── Queries (the TRACE primitive) ──────────────────────────────────────────

  eventsByInteraction(id: InteractionId): LensEvent[] {
    return this.eventsByInteractionId.get(id) ?? [];
  }

  rendersOf(id: ComponentId): RenderEvent[] {
    return this.rendersByComponent.get(id)?.toArray() ?? [];
  }

  getRender(renderId: RenderId): RenderEvent | undefined {
    return this.rendersById.get(renderId);
  }

  snapshot(renderId: RenderId): RenderSnapshot | undefined {
    return this.snapshots.get(renderId);
  }

  instance(id: ComponentId): ComponentInstance | undefined {
    return this.instances.get(id);
  }

  allInstances(): ComponentInstance[] {
    return [...this.instances.values()];
  }

  /** Lifetime render count (not capped by the history ring buffer). */
  renderCount(id: ComponentId): number {
    return this.renderTotals.get(id) ?? 0;
  }

  selfTimeTotal(id: ComponentId): number {
    return this.selfTimeTotals.get(id) ?? 0;
  }

  allEvents(): LensEvent[] {
    return this.events.toArray();
  }

  /**
   * Ordered commits (oldest→newest) for the timeline / time-travel views.
   * Identity-stable until the next mutation — cheap to call during scrubbing.
   */
  commits(): CommitSummary[] {
    if (this.commitsDirty) this.rebuildCommitsFromEvents();
    if (!this.commitsCache) {
      this.commitsCache = this.commitOrder
        .toArray()
        .map((id) => this.summarize(this.commitsById.get(id)!));
    }
    return this.commitsCache;
  }

  commit(commitId: CommitId): CommitSummary | undefined {
    if (this.commitsDirty) this.rebuildCommitsFromEvents();
    const c = this.commitsById.get(commitId);
    return c ? this.summarize(c) : undefined;
  }

  private summarize(c: MutableCommit): CommitSummary {
    return {
      commitId: c.commitId,
      timestamp: c.timestamp,
      endTimestamp: c.endTimestamp,
      componentIds: [...c.components],
      totalSelfTime: c.totalSelfTime,
      ...(c.interactionId !== undefined ? { interactionId: c.interactionId } : {}),
    };
  }

  /** Interaction-first view of the session (redesign §1-2). */
  interactions(): Interaction[] {
    return buildInteractions(this.events.toArray(), (id) => this.instances.get(id)?.name);
  }

  // ── Time travel (historical resolution) ─────────────────────────────────────

  /** Latest render of a component at or before timestamp `t`. */
  renderAtOrBefore(id: ComponentId, t: number): RenderEvent | undefined {
    const buf = this.rendersByComponent.get(id);
    if (!buf || buf.size === 0) return undefined;
    // Renders are oldest→newest: binary-search the rightmost timestamp ≤ t.
    let lo = 0;
    let hi = buf.size - 1;
    let best: RenderEvent | undefined;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const r = buf.at(mid)!;
      if (r.timestamp <= t) {
        best = r;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  /** Snapshot for the render at or before `t` (may be undefined if not retained). */
  snapshotAtOrBefore(id: ComponentId, t: number): RenderSnapshot | undefined {
    const render = this.renderAtOrBefore(id, t);
    return render ? this.snapshots.get(render.renderId) : undefined;
  }

  /** Nearest commit at or before `t` — the commit whose state the cursor shows. */
  commitAt(t: number): CommitSummary | undefined {
    const commits = this.commits(); // oldest→newest, cached
    let lo = 0;
    let hi = commits.length - 1;
    let best: CommitSummary | undefined;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (commits[mid]!.timestamp <= t) {
        best = commits[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  /** Whole-page DOM at or before `t` — offline playback for imported sessions. */
  commitDomAt(t: number): CommitSnapshot | undefined {
    let lo = 0;
    let hi = this.commitSnapshots.size - 1;
    let best: CommitSnapshot | undefined;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cs = this.commitSnapshots.at(mid)!;
      if (cs.timestamp <= t) {
        best = cs;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  /** What the retention caps have taken from this session so far. */
  retention(): RetentionAccount {
    return {
      droppedEvents: this.dropped.events,
      droppedRenders: this.dropped.renders,
      droppedSnapshots: this.dropped.snapshots,
      oldestRetainedAt: this.events.size > 0 ? this.events.at(0)!.timestamp : null,
    };
  }

  stats(): { events: number; renders: number; snapshots: number; components: number } {
    let renders = 0;
    for (const buf of this.rendersByComponent.values()) renders += buf.size;
    return {
      events: this.events.size,
      renders,
      snapshots: this.snapshots.size,
      components: this.instances.size,
    };
  }

  // ── Columnar / viewport queries ────────────────────────────────────────────

  /** Running time bounds from the columnar index (O(1)). */
  timeBounds(): { t0: number; t1: number } {
    return this.timelineIndex.bounds();
  }

  queryTimeline(q: TimelineQuery): TimelineQueryResult {
    return queryTimeline(this.timelineIndex, q);
  }

  statsInRange(
    t0: number,
    t1: number,
    options?: {
      includeLane?: (laneKey: string, name: string) => boolean;
      laneFilter?: TimelineQuery["laneFilter"];
      excludeWasted?: boolean;
    },
  ) {
    return statsInRange(this.timelineIndex, t0, t1, options);
  }

  statsPairInRange(
    t0: number,
    t1: number,
    options?: {
      includeLane?: (laneKey: string, name: string) => boolean;
      laneFilter?: TimelineQuery["laneFilter"];
    },
  ): RegionStatsPair {
    return statsPairInRange(this.timelineIndex, t0, t1, options);
  }

  hitTest(
    t: number,
    preferLane: string | null = null,
    options: HitTestOptions = {},
  ): HitTestResult | null {
    return hitTest(this.timelineIndex, t, preferLane, options);
  }

  activityIntervals(bucketMs?: number): Array<[number, number]> {
    return activityIntervals(this.timelineIndex, bucketMs);
  }

  /**
   * Mark a render as wasted (no observable DOM change) after causality analysis.
   * Timeline reads flags — it never recomputes why().
   */
  setRenderFlag(renderId: RenderId, flag: RenderFlag, on: boolean): void {
    this.timelineIndex.setFlag(renderId as number, flag, on);
  }

  markWasted(renderId: RenderId, wasted: boolean): void {
    this.markWastedMany(wasted ? [renderId] : [], wasted ? [] : [renderId]);
  }

  markWastedMany(wasted: readonly RenderId[], expected: readonly RenderId[] = []): void {
    this.timelineIndex.setWastedFlags([
      ...wasted.map((renderId) => ({ renderId: renderId as number, wasted: true })),
      ...expected.map((renderId) => ({ renderId: renderId as number, wasted: false })),
    ]);
    for (const renderId of wasted) {
      const render = this.rendersById.get(renderId);
      if (render) this.flatTree.setLastObservable(render.componentId as number, false);
    }
    for (const renderId of expected) {
      const render = this.rendersById.get(renderId);
      if (render) this.flatTree.setLastObservable(render.componentId as number, true);
    }
  }

  markRenderObservable(renderId: RenderId, observable: boolean): void {
    const render = this.rendersById.get(renderId);
    if (render) this.flatTree.setLastObservable(render.componentId as number, observable);
  }

  configureRetention(config: Partial<RetentionConfig>): void {
    Object.assign(this.retentionManager.config, config);
  }

  private appendTimeline(event: RenderEvent): void {
    const inst = this.instances.get(event.componentId);
    const name = inst?.name ?? `#${event.componentId}`;
    const laneKey = `t:${name}`;
    const total = Math.max(event.totalDuration, event.selfDuration, MIN_CLIP_MS);
    this.timelineIndex.append({
      timestamp: event.timestamp,
      duration: total,
      selfDuration: event.selfDuration,
      renderId: event.renderId as number,
      componentId: event.componentId as number,
      commitId: event.commitId as number,
      cause: causeFromReasons(event.reasons),
      name,
      laneKey,
    });
  }

  /**
   * Spill events older than the HOT window into WARM chunks (and eventually COLD).
   * The live index retains HOT+WARM for queries; COLD is loaded on zoom-in.
   */
  private maybeSpillWarm(): void {
    const newest = this.timelineIndex.t1;
    if (!Number.isFinite(newest)) return;
    const cutoff = this.retentionManager.hotCutoff(newest);
    // Only spill when we have substantial history beyond HOT.
    if (this.timelineIndex.t0 >= cutoff - 1) return;
    if (this.timelineIndex.count < this.retentionManager.config.chunkEvents) return;
    const chunk = sliceToChunk({
      t0: this.timelineIndex.t0,
      t1: cutoff,
      count: this.timelineIndex.count,
      timestamps: this.timelineIndex.timestamps,
      durations: this.timelineIndex.durations,
      selfDurations: this.timelineIndex.selfDurations,
      renderIds: this.timelineIndex.renderIds,
      componentIds: this.timelineIndex.componentIds,
      commitIds: this.timelineIndex.commitIds,
      causes: this.timelineIndex.causes,
      flags: this.timelineIndex.flags,
      laneIndices: this.timelineIndex.laneIndices,
      laneOrder: this.timelineIndex.laneOrder,
    });
    if (chunk && chunk.count > 0) {
      this.retentionManager.pushWarm(chunk);
      void this.retentionManager.spillWarmToCold();
    }
  }

  // ── Subscriptions ───────────────────────────────────────────────────────────

  subscribe(selector: TraceSelector, callback: () => void): Dispose {
    const sub: Subscription = { selector, callback };
    this.subscriptions.add(sub);
    return () => {
      this.subscriptions.delete(sub);
    };
  }

  private notify(
    touchedComponents: Set<ComponentId>,
    touchedInteractions: Set<InteractionId>,
  ): void {
    for (const sub of this.subscriptions) {
      if (this.matches(sub.selector, touchedComponents, touchedInteractions)) {
        sub.callback();
      }
    }
  }

  private matches(
    selector: TraceSelector,
    components: Set<ComponentId>,
    interactions: Set<InteractionId>,
  ): boolean {
    switch (selector.kind) {
      case "global":
        return true;
      case "component":
        return components.has(selector.id);
      case "interaction":
        return interactions.has(selector.id);
    }
  }

  /**
   * Drop everything. The panel calls this at a session boundary: every id
   * factory lives in the inspected page and restarts at 1 on each document
   * load, so carrying the previous document's log over would make the new
   * one's renders look like duplicates and silently drop them.
   */
  clear(): void {
    this.events.clear();
    this.rendersByComponent.clear();
    this.rendersById.clear();
    this.seenEventIds.clear();
    this.snapshots.clear();
    this.snapshotOrder.clear();
    this.instances.clear();
    this.renderTotals.clear();
    this.selfTimeTotals.clear();
    this.eventsByInteractionId.clear();
    this.commitsById.clear();
    this.commitOrder.clear();
    this.commitSnapshots.clear();
    this.dropped = { events: 0, renders: 0, snapshots: 0 };
    this.commitsDirty = false;
    this.commitsCache = null;
    this.timelineIndex.clear();
    this.flatTree.clear();
    this.retentionManager.clear();
    this.treeDirty = false;
    // Wake subscribers (Tree/Inspector/Timeline) so they re-render to empty.
    this.notify(new Set(), new Set());
  }
}

/** A copy at a new capacity, keeping the newest entries. */
function resized<T>(buffer: RingBuffer<T>, capacity: number): RingBuffer<T> {
  if (buffer.capacity === capacity) return buffer;
  const next = new RingBuffer<T>(capacity);
  for (const item of buffer.toArray().slice(-capacity)) next.push(item);
  return next;
}
